import {
  IMultiSelectControlField2,
  ISelectOption,
  toFormFields2
} from 'src/lib';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchScenarioFilters,
  fetchScenarioNames,
  fetchScenarioList,
  fetchPhRecommendationConfig
} from '../api/scenario-api';
import { IScenarioState } from '../types/scenario-state.model';
import { IScenarioSummary, Scenario } from '../types/scenario.model';

const initialState: IScenarioState = {
  savingScenarioData: false,
  listOfScenarios: [],
  fetchingListOfScenarios: false,
  selectedScenarios: [],
  fetchingScenarioFilters: false,
  fetchingScenarioNames: false,
  scenarioNames: [],
  scenarioFilters: {
    filters: [],
    combinations: [],
    initialValues: {},
    ph_updated_at:{}
  },
  fetchingPhRecommendationConfig: false,
  phRecommendationConfig: {
    ph_legend_map_object: {
      invest: {},
      delist: {},
      maintain: {}
    }
  }
};

const getListOfSavedScenarios = createAsyncThunk(
  'getListOfSavedScenarios',
  async (_args: { source_id: number }, thunkAPI) => {
    const response = await fetchScenarioList(_args);
    return response;
  }
);

const getScenarioFilters = createAsyncThunk(
  '/api/filters/getScenarioFilters',
  async (args: { sourceId: number; moduleId: number }) => {
    const response = await fetchScenarioFilters(args.sourceId, args.moduleId);
    return response;
  }
);

const getScenarioNames = createAsyncThunk(
  '/api/scenarios/getScenarioNames',
  async (sourceId: number) => {
    const response = await fetchScenarioNames({ sourceId });
    return response;
  }
);

// async thunk for getting ph recommendation config
const getPhRecommendationConfig = createAsyncThunk(
  'getPhRecommendationConfig',
  async (sourceId: number) => {
    const response = await fetchPhRecommendationConfig(sourceId);
    return response;
  }
);

const mutateScenario =
  (isSelected: boolean) =>
  (scenario: Scenario): Scenario => ({
    ...scenario,
    isSelected
  });

const scenarioSlice = createSlice({
  name: 'scenario',
  initialState,
  reducers: {
    updateFilterFieldOptions(
      state,
      action: PayloadAction<{ [filterName: string]: ISelectOption[] }>
    ) {
      const remainingFilterValues = action.payload;
      state.scenarioFilters.filters = state.scenarioFilters.filters.map(
        (filter) => {
          if (remainingFilterValues[filter.name]) {
            return { ...filter, values: remainingFilterValues[filter.name] };
          }
          return filter;
        }
      );
    },
    toggleSelectionScenario(
      state,
      action: PayloadAction<{
        scenario: Scenario;
        isSelected: boolean;
        isMultiple: boolean;
      }>
    ) {
      const {
        scenario: toggledScenario,
        isSelected,
        isMultiple
      } = action.payload;
      state.selectedScenarios = [];
      const scenarios = state.listOfScenarios.map((scenario) =>
        isMultiple ? scenario : { ...scenario, isSelected: false }
      );
      state.listOfScenarios = scenarios;
      const scenarioIndex = scenarios.findIndex(
        (scenario) => scenario.pk_id === toggledScenario.pk_id
      );
      const scenarioToToggle = scenarios[scenarioIndex];
      if (!scenarioToToggle) {
        console.error('Could not find the scenario to toggle');
        return;
      }
      scenarioToToggle.isSelected = isSelected;
      // eslint-disable-next-line no-nested-ternary
      state.selectedScenarios = isMultiple
        ? scenarios.filter((scenario) => scenario.isSelected)
        : isSelected
        ? [scenarioToToggle]
        : [];
    },
    resetSelection(state) {
      state.selectedScenarios = [];
      state.listOfScenarios = [];
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getListOfSavedScenarios.pending, (state) => {
        state.fetchingListOfScenarios = true;
      })
      .addCase(getListOfSavedScenarios.fulfilled, (state, action) => {
        state.selectedScenarios = [];
        state.fetchingListOfScenarios = false;
        state.listOfScenarios = action.payload.data.map(mutateScenario(false));
      })
      .addCase(getListOfSavedScenarios.rejected, (state) => {
        state.selectedScenarios = [];
        state.fetchingListOfScenarios = false;
      })
      .addCase(getScenarioNames.pending, (state) => {
        state.fetchingScenarioNames = true;
      })
      .addCase(getScenarioNames.fulfilled, (state, action) => {
        state.fetchingScenarioNames = false;
        state.scenarioNames = action.payload.data.map((scenario) => ({
          pk_id: scenario.pk_id,
          name: scenario.name.toLowerCase()
        }));
      })
      .addCase(getScenarioNames.rejected, (state) => {
        state.fetchingScenarioNames = false;
      })
      .addCase(getScenarioFilters.pending, (state) => {
        state.fetchingScenarioFilters = true;
      })
      .addCase(getScenarioFilters.rejected, (state) => {
        state.fetchingScenarioFilters = false;
      })
      .addCase(getScenarioFilters.fulfilled, (state, action) => {
        state.fetchingScenarioFilters = false;
        const filters: IMultiSelectControlField2[] = toFormFields2(
          action.payload.filters
            .slice()
            .sort(
              (filterA, filterB) =>
                filterA.placement_order - filterB.placement_order
            )
        );
        const { combinations,ph_updated_at} = action.payload;
        const filterToValuesMap = filters.reduce<{
          [key: string]: ISelectOption[];
        }>(
          (previousValue, currentValue) => ({
            ...previousValue,
            ...{
              [currentValue.name]: currentValue.values
            }
          }),
          {}
        );
        state.scenarioFilters.initialValues = filterToValuesMap;
        state.scenarioFilters.combinations = combinations;
        state.scenarioFilters.filters = filters.map(
          (filter: IMultiSelectControlField2) => {
            const filterName = filter.name;
            if (filterToValuesMap[filterName]) {
              // eslint-disable-next-line no-param-reassign
              filter.values = filterToValuesMap[filterName].slice();
            }
            return filter;
          }
        );
        // storing ph recommendation date 
        state.scenarioFilters.ph_updated_at = ph_updated_at
      })

      .addCase(getPhRecommendationConfig.pending, (state) => {
        state.fetchingPhRecommendationConfig = true;
      })
      .addCase(getPhRecommendationConfig.fulfilled, (state, action) => {
        state.fetchingPhRecommendationConfig = false;
        state.phRecommendationConfig = action.payload['ph_legend_map_object'];
      })
      .addCase(getPhRecommendationConfig.rejected, (state) => {
        state.fetchingPhRecommendationConfig = false;
      })
});

export const {
  toggleSelectionScenario,
  resetSelection,
  updateFilterFieldOptions
} = scenarioSlice.actions;

export {
  getListOfSavedScenarios,
  getScenarioFilters,
  getScenarioNames,
  getPhRecommendationConfig
};

export default scenarioSlice;
